import React from 'react';
import { Chart } from 'react-google-charts';
import { ChartConfig } from '../Chart';

interface ChartPreviewProps {
  config: ChartConfig;
  width?: string;
  height?: string;
}

const CHART_FONT_NAME = 'Arial';
const ANNOTATION_FONT_SIZE = 15;
const ANNOTATION_FONT_BOLD = true;

export const ChartPreview: React.FC<ChartPreviewProps> = ({
  config,
  height = '300px',
}) => {
  // Preparar dados para Google Charts
  const prepareChartData = () => {
    // Gráficos de linha sempre usam múltiplas séries
    if (config.type === 'line') {
      // Se tem multipleSeriesData, usar
      if (config.multipleSeriesData) {
        const { series, categories, values } = config.multipleSeriesData;

        if (series.length === 0 || categories.length === 0) {
          const emptyHeader: any[] = ['Categoria'];
          series.forEach((s) => {
            emptyHeader.push(s.name);
            emptyHeader.push({ role: 'annotation', type: 'string' });
          });
          const emptyRow: any[] = ['Sem dados'];
          series.forEach(() => {
            emptyRow.push(0);
            emptyRow.push(''); // Anotação vazia
          });
          return [emptyHeader, emptyRow];
        }

        // Para gráficos de linha, adicionar colunas de anotação para cada série
        // Formato: [Categoria, Série1, {role: 'annotation', type: 'string'}, Série2, {role: 'annotation', type: 'string'}, ...]
        const header: any[] = ['Categoria'];
        series.forEach((s) => {
          header.push(s.name);
          header.push({ role: 'annotation', type: 'string' });
        });

        const rows = categories.map((category, catIdx) => {
          const row: any[] = [category];
          series.forEach((_, serieIdx) => {
            const value = values[serieIdx]?.[catIdx] || 0;
            row.push(value);
            row.push(String(value)); // Anotação com o valor
          });
          return row;
        });

        return [header, ...rows];
      }
      // Se não tem multipleSeriesData mas tem data (modo antigo), converter
      if (config.data && config.data.length > 0) {
        const series = [
          {
            name: 'Série 1',
            color: config.data[0]?.color || '#000000',
          },
        ];
        const categories = config.data.map((item) => item.label);
        const values = [config.data.map((item) => item.value)];

        // Adicionar colunas de anotação
        const header: any[] = ['Categoria'];
        series.forEach((s) => {
          header.push(s.name);
          header.push({ role: 'annotation', type: 'string' });
        });

        const rows = categories.map((category, catIdx) => {
          const row: any[] = [category];
          series.forEach((_, serieIdx) => {
            const value = values[serieIdx]?.[catIdx] || 0;
            row.push(value);
            row.push(String(value)); // Anotação com o valor
          });
          return row;
        });

        return [header, ...rows];
      }
      // Fallback quando não há dados
      return [
        ['Categoria', 'Série 1', { role: 'annotation', type: 'string' }],
        ['Sem dados', 0, ''],
      ];
    }

    // Verificar se é modo múltiplas séries para column e bar
    if (
      config.mode === 'multiple' &&
      config.multipleSeriesData &&
      (config.type === 'column' || config.type === 'bar')
    ) {
      const { series, categories, values } = config.multipleSeriesData;

      if (series.length === 0 || categories.length === 0) {
        const emptyHeader: any[] = ['Categoria'];
        series.forEach((s) => {
          emptyHeader.push(s.name);
          emptyHeader.push({ role: 'annotation', type: 'string' });
        });
        const emptyRow: any[] = ['Sem dados'];
        series.forEach(() => {
          emptyRow.push(0);
          emptyRow.push(''); // Anotação vazia
        });
        return [emptyHeader, emptyRow];
      }

      // Adicionar colunas de anotação para cada série
      const header: any[] = ['Categoria'];
      series.forEach((s) => {
        header.push(s.name);
        header.push({ role: 'annotation', type: 'string' });
      });

      // Linhas seguintes: cada linha é uma categoria com valores de cada série
      const rows = categories.map((category, catIdx) => {
        const row: any[] = [category];
        series.forEach((_, serieIdx) => {
          const value = values[serieIdx]?.[catIdx] || 0;
          row.push(value);
          row.push(String(value)); // Anotação com o valor
        });
        return row;
      });

      return [header, ...rows];
    }

    // Modo simples ou pie/line
    if (!config.data || config.data.length === 0) {
      return [
        ['Label', 'Value'],
        ['Sem dados', 1],
      ];
    }

    switch (config.type) {
      case 'pie': {
        // Para gráfico de pizza, usar apenas o nome do item (valores aparecem na legenda customizada)
        return [
          ['Label', 'Value'],
          ...config.data.map((item) => [item.label, item.value]),
        ];
      }

      case 'column':
      case 'bar': {
        // Modo simples: usar formato simples com role: "style" para cores individuais
        if (config.mode === 'simple' || !config.mode) {
          // Formato: [Label, Value, {role: "style"}, {role: "annotation"}]
          return [
            ['Categoria', 'Valor', { role: 'style' }, { role: 'annotation' }],
            ...config.data.map((item) => [
              item.label,
              item.value,
              item.color,
              String(item.value),
            ]),
          ];
        }
        // Fallback (não deveria acontecer)
        return [
          ['Label', 'Value'],
          ...config.data.map((item) => [item.label, item.value]),
        ];
      }

      default:
        return [
          ['Label', 'Value'],
          ['Sem dados', 1],
        ];
    }
  };

  const getChartOptions = () => {
    // Altura do gráfico (legenda será customizada fora)
    // Usar altura fixa padrão para todos os gráficos de barras para manter consistência
    const chartHeightValue = parseInt(height.replace('px', '')) || 300;

    const baseOptions = {
      title: undefined, // Título será mostrado externamente no topo
      backgroundColor: 'transparent',
      height: chartHeightValue,
      legend: 'none' as const, // Esconder legenda padrão - será customizada
      fontName: CHART_FONT_NAME,
    };

    switch (config.type) {
      case 'pie': {
        // Configurar estilo do gráfico de pizza
        const is3D = config.pieStyle === '3d';
        const pieHole = config.pieStyle === 'donut' ? 0.5 : undefined;

        // Configurar tipo de texto nas fatias
        const pieSliceTextValue = config.pieSliceText || 'value';

        return {
          ...baseOptions,
          enableInteractivity: false,
          ...(is3D && { is3D: true }),
          ...(pieHole !== undefined && { pieHole }),
          colors: config.data.map((item) => item.color),
          pieSliceText: pieSliceTextValue, // 'value' ou 'percentage'
          pieSliceTextStyle: {
            fontName: CHART_FONT_NAME,
            fontSize: ANNOTATION_FONT_SIZE,
            bold: ANNOTATION_FONT_BOLD,
            color: '#000000',
          },
          sliceVisibilityThreshold: 0.2,
          // Área do gráfico otimizada (mais espaço já que legenda é customizada)
          chartArea: {
            top: 20,
            left: 20,
            bottom: 20,
            right: 20,
            width: '100%',
            height: '85%',
          },
        };
      }

      case 'column': {
        // Calcular o valor máximo para adicionar espaço no eixo Y
        const calculateMaxValue = (): number => {
          let maxValue = 0;

          if (config.mode === 'multiple' && config.multipleSeriesData) {
            config.multipleSeriesData.values.forEach((serieValues) => {
              serieValues.forEach((value) => {
                if (value > maxValue) {
                  maxValue = value;
                }
              });
            });
          } else if (config.data && config.data.length > 0) {
            config.data.forEach((item) => {
              if (item.value > maxValue) {
                maxValue = item.value;
              }
            });
          }

          // Adicionar 30% de margem acima do valor máximo para dar espaço aos labels
          return maxValue > 0 ? maxValue * 1.3 : 10;
        };

        const maxValue = calculateMaxValue();

        // Configuração de annotations para valores
        const annotationsConfig = {
          textStyle: {
            fontName: CHART_FONT_NAME,
            fontSize: ANNOTATION_FONT_SIZE,
            bold: ANNOTATION_FONT_BOLD,
            color: '#000000', // Preto sempre
          },
          alwaysOutside: true, // Valores sempre fora das barras
        };

        if (config.mode === 'multiple' && config.multipleSeriesData) {
          // Modo múltiplas séries
          return {
            ...baseOptions,
            colors: config.multipleSeriesData.series.map((s) => s.color),
            hAxis: {
              minValue: 0,
              textStyle: { fontName: CHART_FONT_NAME },
            },
            vAxis: {
              minValue: 0,
              viewWindow: {
                max: maxValue,
              },
              textStyle: { fontName: CHART_FONT_NAME },
            },
            legend: {
              position: 'top',
              textStyle: {
                fontName: CHART_FONT_NAME,
                fontSize: 12,
              },
            },
            isStacked: false, // Gráfico agrupado, não empilhado
            annotations: annotationsConfig,
            chartArea: {
              width: '90%',
              height: '80%',
              left: 50,
              top: 50, // Aumentar espaço superior para separar a legenda do gráfico
            },
          };
        }
        // Modo simples - cada barra é uma série separada para permitir cores individuais
        return {
          ...baseOptions,
          colors: config.data.map((item) => item.color),
          hAxis: {
            minValue: 0,
            textStyle: { fontName: CHART_FONT_NAME },
          },
          vAxis: {
            minValue: 0,
            viewWindow: {
              max: maxValue,
            },
            textStyle: { fontName: CHART_FONT_NAME },
          },
          legend: 'none' as const,
          // Mostrar valores nas colunas sempre visíveis e fora das barras
          annotations: {
            textStyle: {
              fontName: CHART_FONT_NAME,
              fontSize: ANNOTATION_FONT_SIZE,
              bold: ANNOTATION_FONT_BOLD,
              fillColor: '#000000',
              color: '#000000', // Preto sempre
            },
            alwaysOutside: true, // Valores sempre fora das barras
          },
          // Forçar cor preta nas annotations globalmente
          annotationTextStyle: {
            fontName: CHART_FONT_NAME,
            fontSize: ANNOTATION_FONT_SIZE,
            bold: ANNOTATION_FONT_BOLD,
            color: '#000000',
          },
          // Habilitar dados sempre visíveis com opacidade total
          dataOpacity: 1,
          chartArea: {
            width: '90%',
            height: '80%',
            left: 50,
            top: 20,
          },
        };
      }

      case 'bar': {
        // Calcular o valor máximo para adicionar espaço no eixo X (barra horizontal)
        const calculateMaxValue = (): number => {
          let maxValue = 0;

          if (config.mode === 'multiple' && config.multipleSeriesData) {
            config.multipleSeriesData.values.forEach((serieValues) => {
              serieValues.forEach((value) => {
                if (value > maxValue) {
                  maxValue = value;
                }
              });
            });
          } else if (config.data && config.data.length > 0) {
            config.data.forEach((item) => {
              if (item.value > maxValue) {
                maxValue = item.value;
              }
            });
          }

          // Adicionar 30% de margem acima do valor máximo para dar espaço aos labels
          return maxValue > 0 ? maxValue * 1.3 : 10;
        };

        const maxValue = calculateMaxValue();

        // Configuração de annotations para valores
        const annotationsConfigBar = {
          textStyle: {
            fontName: CHART_FONT_NAME,
            fontSize: ANNOTATION_FONT_SIZE,
            bold: ANNOTATION_FONT_BOLD,
            color: '#000000', // Preto sempre
          },
          alwaysOutside: true, // Valores sempre fora das barras
        };

        if (config.mode === 'multiple' && config.multipleSeriesData) {
          // Modo múltiplas séries
          return {
            ...baseOptions,
            colors: config.multipleSeriesData.series.map((s) => s.color),
            hAxis: {
              minValue: 0,
              viewWindow: {
                max: maxValue,
              },
              textStyle: { fontName: CHART_FONT_NAME },
            },
            vAxis: {
              minValue: 0,
              textStyle: {
                fontName: CHART_FONT_NAME,
                fontSize: 12,
              },
              allowContainerBoundaryTextCuts: true, // Permite quebra de linha nos labels
            },
            legend: {
              position: 'top',
              textStyle: {
                fontName: CHART_FONT_NAME,
                fontSize: 12,
              },
            },
            isStacked: false, // Gráfico agrupado, não empilhado
            annotations: annotationsConfigBar,
            chartArea: {
              width: '80%', // Largura ajustada para dar espaço aos labels
              height: '85%',
              left: 100, // Espaço à esquerda para os labels das categorias (reduzido)
              top: 20,
            },
          };
        }
        // Modo simples - cada barra é uma série separada para permitir cores individuais
        return {
          ...baseOptions,
          colors: config.data.map((item) => item.color),
          hAxis: {
            minValue: 0,
            viewWindow: {
              max: maxValue,
            },
            textStyle: { fontName: CHART_FONT_NAME },
          },
          vAxis: {
            minValue: 0,
            textStyle: {
              fontName: CHART_FONT_NAME,
              fontSize: 12,
            },
            allowContainerBoundaryTextCuts: true, // Permite quebra de linha nos labels
          },
          legend: 'none' as const,
          // Mostrar valores nas barras sempre visíveis e fora das barras
          annotations: {
            textStyle: {
              fontName: CHART_FONT_NAME,
              fontSize: ANNOTATION_FONT_SIZE,
              bold: ANNOTATION_FONT_BOLD,
              color: '#000000', // Preto sempre
            },
            alwaysOutside: true, // Valores sempre fora das barras
          },
          // Forçar cor preta nas annotations globalmente
          annotationTextStyle: {
            fontName: CHART_FONT_NAME,
            fontSize: ANNOTATION_FONT_SIZE,
            bold: ANNOTATION_FONT_BOLD,
            color: '#000000',
          },
          dataOpacity: 1,
          chartArea: {
            width: '80%', // Largura ajustada para dar espaço aos labels
            height: '85%',
            left: 100, // Espaço à esquerda para os labels das categorias (reduzido)
            top: 20,
          },
        };
      }

      case 'line': {
        // Opções comuns para annotations (valores nos pontos)
        const annotationsConfigLine = {
          textStyle: {
            fontName: CHART_FONT_NAME,
            fontSize: ANNOTATION_FONT_SIZE,
            bold: ANNOTATION_FONT_BOLD,
            color: '#000000', // Preto sempre
          },
          alwaysOutside: true, // Valores sempre fora dos pontos para evitar problemas de contraste
        };

        // Calcular o valor máximo dos dados para adicionar espaço no eixo Y
        const calculateMaxValue = (): number => {
          let maxValue = 0;

          if (config.multipleSeriesData) {
            // Encontrar o maior valor em todas as séries
            config.multipleSeriesData.values.forEach((serieValues) => {
              serieValues.forEach((value) => {
                if (value > maxValue) {
                  maxValue = value;
                }
              });
            });
          } else if (config.data && config.data.length > 0) {
            // Modo antigo: usar valores do data
            config.data.forEach((item) => {
              if (item.value > maxValue) {
                maxValue = item.value;
              }
            });
          }

          // Adicionar 30% de margem acima do valor máximo para dar espaço aos labels acima dos pontos
          return maxValue > 0 ? maxValue * 1.3 : 10;
        };

        const maxValue = calculateMaxValue();

        // Gráficos de linha sempre usam múltiplas séries
        if (config.multipleSeriesData) {
          return {
            ...baseOptions,
            colors: config.multipleSeriesData.series.map((s) => s.color),
            hAxis: {
              textStyle: { fontName: CHART_FONT_NAME },
              showTextEvery: 1, // Mostrar todas as categorias no eixo X
              slantedText: false, // Não inclinar o texto
            },
            vAxis: {
              minValue: 0,
              viewWindow: {
                max: maxValue,
              },
              textStyle: { fontName: CHART_FONT_NAME },
            },
            legend: {
              position: 'top',
              textStyle: {
                fontName: CHART_FONT_NAME,
                fontSize: 12,
              },
            },
            curveType: 'function',
            annotations: annotationsConfigLine,
            pointSize: 5, // Tamanho dos pontos para melhor visualização
            pointShape: 'circle',
            chartArea: {
              width: '90%',
              height: '70%',
              left: 50,
              top: 50, // Aumentar espaço superior para separar a legenda do gráfico
              bottom: 50, // Espaço para os labels do eixo X
            },
          };
        }
        // Fallback se não tiver multipleSeriesData (dados antigos ou vazio)
        // Se tiver data antiga, usar a cor do primeiro item
        const fallbackColor =
          config.data && config.data.length > 0
            ? config.data[0].color
            : '#000000';
        return {
          ...baseOptions,
          colors: [fallbackColor],
          hAxis: {
            textStyle: { fontName: CHART_FONT_NAME },
            showTextEvery: 1, // Mostrar todas as categorias no eixo X
            slantedText: false, // Não inclinar o texto
          },
          vAxis: {
            minValue: 0,
            viewWindow: {
              max: maxValue,
            },
            textStyle: { fontName: CHART_FONT_NAME },
          },
          legend: {
            position: 'top',
            textStyle: {
              fontName: CHART_FONT_NAME,
              fontSize: 12,
            },
          },
          curveType: 'function',
          annotations: annotationsConfigLine,
          pointSize: 5,
          pointShape: 'circle',
          chartArea: {
            width: '90%',
            height: '70%',
            left: 50,
            top: 50, // Aumentar espaço superior para separar a legenda do gráfico
            bottom: 50, // Espaço para os labels do eixo X
          },
        };
      }

      default:
        return baseOptions;
    }
  };

  const chartData = prepareChartData();
  const chartOptions = getChartOptions();

  // Função para renderizar título externo no topo
  const renderTitle = () => {
    if (!config.title) {
      return null;
    }

    return (
      <div className="mb-4" style={{ fontFamily: CHART_FONT_NAME }}>
        <h3
          className="text-base font-normal text-gray-800"
          style={{ fontFamily: CHART_FONT_NAME }}
        >
          {config.title}
        </h3>
      </div>
    );
  };

  // Função para renderizar legenda customizada
  const renderCustomLegend = () => {
    if (!config.data || config.data.length === 0 || config.type !== 'pie') {
      return null;
    }

    const total = config.data.reduce((sum, item) => sum + item.value, 0);

    return (
      <div
        className="flex flex-col items-start mt-4 space-y-2"
        style={{ fontFamily: CHART_FONT_NAME }}
      >
        {/* Itens da legenda */}
        {config.data.map((item, index) => {
          const percentage = ((item.value / total) * 100).toFixed(1);
          return (
            <div key={index} className="flex items-center">
              <div
                className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span
                className="text-gray-700"
                style={{ fontFamily: CHART_FONT_NAME, fontSize: '13px' }}
              >
                {item.label} - {item.value} ({percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const getChartType = () => {
    switch (config.type) {
      case 'pie':
        return 'PieChart';
      case 'column':
        return 'ColumnChart';
      case 'bar':
        return 'BarChart';
      case 'line':
        return 'LineChart';
      default:
        return 'PieChart';
    }
  };

  // Verificar se há dados disponíveis
  const hasData =
    // Gráficos de linha sempre usam múltiplas séries
    (config.type === 'line' &&
      config.multipleSeriesData &&
      config.multipleSeriesData.series.length > 0 &&
      config.multipleSeriesData.categories.length > 0) ||
    // Outros gráficos com múltiplas séries
    (config.mode === 'multiple' &&
      config.multipleSeriesData &&
      config.multipleSeriesData.series.length > 0 &&
      config.multipleSeriesData.categories.length > 0) ||
    // Modo simples
    (config.data && config.data.length > 0);

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <p>Nenhum dado disponível</p>
          <p className="text-sm">Adicione itens ao gráfico</p>
        </div>
      </div>
    );
  }

  // Usar altura padrão, legenda será customizada fora do gráfico
  const displayHeight = height;

  return (
    <div>
      {renderTitle()}
      <div
        className={`editor-chart-container chart-${config.type}`}
        style={{ height: displayHeight, overflow: 'hidden' }}
      >
        <Chart
          chartType={
            getChartType() as
              | 'PieChart'
              | 'ColumnChart'
              | 'BarChart'
              | 'LineChart'
          }
          data={chartData}
          options={chartOptions}
          height={displayHeight}
        />
      </div>
      {renderCustomLegend()}
    </div>
  );
};

export default ChartPreview;
